import { useEffect, useState } from "react"

type Props = {
  background?: string
}

export const BlobBackground = (props: Props) => {
  const [canvas, setCanvas] = useState<HTMLCanvasElement>()
  const debug = false

  useEffect(() => {
    if (canvas == null) return
    const ctx = canvas.getContext("2d")

    let mounted = true

    const resizeCanvas = () => {
      const devicePixelRatio = window.devicePixelRatio || 1

      const cssWidth = canvas.clientWidth
      const cssHeight = canvas.clientHeight

      canvas.width = cssWidth * devicePixelRatio
      canvas.height = cssHeight * devicePixelRatio

      ctx.scale(devicePixelRatio, devicePixelRatio)
    }

    resizeCanvas()
    window.addEventListener("resize", resizeCanvas)

    class State {
      private _frame = 0
      private _count = 1

      constructor(count: number, frame = 0) {
        this._count = count
        this._frame = frame
      }

      get frame(): number {
        return this._frame
      }

      get count(): number {
        return this._count
      }

      nextFrame() {
        this._frame++
      }

      getUpdatedPosition = () => {
      const devicePixelRatio = window.devicePixelRatio || 1

        const newX = Math.cos(this._frame * 0.001) * (canvas.width / (3.5 * devicePixelRatio)) // Modify amplitude
        const newY = Math.sin(this._frame * 0.00025) * (canvas.height / (2.5 * devicePixelRatio)) // Adjust frequency for variety
        return { x: newX, y: newY }
      }
    }
    const COUNT = 18
    const OUTER_RADIUS = 300
    const state = new State(COUNT < 50 ? COUNT : 50, +localStorage.getItem("blobFrame") ?? 0)

    const loop = () => {
      if (!mounted) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      for (let i = 0; i < state.count; i++) {
        const radius = OUTER_RADIUS / (i + 0.8) - OUTER_RADIUS
        const alpha = (i + 1) / state.count
        const updatedPosition = state.getUpdatedPosition()
        drawControlledCircle({
          ctx,
          cx: window.innerWidth / 2 + updatedPosition.x,
          cy: window.innerHeight / 2 + updatedPosition.y,
          radius,
          alpha,
          points: 250,
          frame: state.frame - i,
          hue: i * 2.4 + state.frame
        })
      }

      state.nextFrame()
      requestAnimationFrame(loop)
    }

    requestAnimationFrame(loop)

    return () => {
      mounted = false
      window.removeEventListener("resize", resizeCanvas)
      localStorage.setItem("blobFrame", JSON.stringify(state.frame))
    }
  }, [canvas])

  return (
    <canvas
      ref={setCanvas}
      style={{
        position: "absolute",
        background: props.background,
        width: "100%",
        height: "100%"
      }}
    />
  )
}

function drawControlledCircle(options: {
  ctx: CanvasRenderingContext2D
  cx: number
  cy: number
  radius: number
  alpha: number
  points: number
  frame: number
  hue: number
}) {
  const SPEED = -0.005
  const FREQUENCY = 4

  const { ctx, cx, cy, radius, alpha, points, frame, hue } = options

  ctx.beginPath()

  const centerX = window.innerWidth / 2
  const centerY = window.innerHeight / 2

  const angleStep = (2 * Math.PI) / points
  // const speed = SPEED > -0.5 && SPEED < 0.5 ? SPEED : SPEED < -0.5 ? -0.5 : 0.5
  const speed = SPEED

  Array(points)
    .fill({})
    .map((_obj, i) => {
      const angle = i * angleStep
      const rotationAmt = (frame / 10) * speed
      const ampTheta = Math.cos(frame / 200) * 12
      const ampMultiplier = Math.sin(angle) * ampTheta * 2
      const freqMultiplier = Math.cos(i * 2 * Math.PI) * Math.min(FREQUENCY, 20)

      const sineOffset =
        Math.sin(frame * speed + angle * freqMultiplier) * ampMultiplier
      const offsetRadius = radius + sineOffset

      const SCALE_X = (Math.sin(frame * 0.003) / 2 + 1) * 1

      const circle = {
        x: cx + offsetRadius * Math.cos(angle) * SCALE_X,
        y: cy + offsetRadius * Math.sin(angle)
      }

      const relativeX = circle.x - centerX
      const relativeY = circle.y - centerY

      const rotatedCircle = {
        x:
          Math.cos(rotationAmt) * relativeX - Math.sin(rotationAmt) * relativeY,
        y: Math.sin(rotationAmt) * relativeX + Math.cos(rotationAmt) * relativeY
      }
      return { x: rotatedCircle.x + centerX, y: rotatedCircle.y + centerY }
    })
    .forEach((coord, i) => {
      if (i === 0) {
        ctx.moveTo(coord.x, coord.y)
      } else {
        ctx.lineTo(coord.x, coord.y)
      }
    })

  ctx.closePath()
  ctx.strokeStyle = `hsla(${hue % 360}, 100%, 50%, ${alpha})`
  ctx.stroke()
}
